import React, { useState, useEffect, useCallback, useRef } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import "../../components/DateTimePicker/dateTimePicker.css";
import "../../components/DateTimePicker/materialUIOverrides.css";
import moment from "moment";
import DealFormBasics from "./DealFormBasics";
import "./dealForm.css";
import useUnsavedChangesGuard from "../../hooks/useUnsavedChangesGuard";

const CreateDeal = () => {
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const { profileData } = useSelector((state) => state.auth);

  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  const [formState, setFormState] = useState({
    dealName: "",
    dealDescription: "",
    dealPrice: "",
    dealOrder: "0",
    dealPricingType: "CustomPrice",
    dealDiscountPercentage: "",
    startDate: null,
    endDate: null,
  });

  const [errors, setErrors] = useState({
    dealName: "",
    dealPrice: "",
  });

  const [toggles, setToggles] = useState({
    isActive: true,
    showInKiosk: true,
    showInPOS: true,
    showInOnline: true,
  });

  const [imageState, setImageState] = useState({
    dealImage: "",
    imageFile: null,
  });

  const [loading, setLoading] = useState(false);
  const [createdBy, setCreatedBy] = useState("");
  const [compCurrency, setCompCurrency] = useState("GBP");
  const initialFormSnapshotRef = useRef(null);

  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };

  const getCurrencySymbol = (currencyCode = compCurrency) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  const getFormSnapshot = useCallback(() => {
    const start =
      formState.startDate && typeof formState.startDate.toISOString === "function"
        ? formState.startDate.toISOString()
        : formState.startDate
          ? String(formState.startDate)
          : null;
    const end =
      formState.endDate && typeof formState.endDate.toISOString === "function"
        ? formState.endDate.toISOString()
        : formState.endDate
          ? String(formState.endDate)
          : null;
    return JSON.stringify({
      formState: { ...formState, startDate: start, endDate: end },
      toggles,
      dealImage: imageState.dealImage || "",
      imageFileName: imageState.imageFile?.name || "",
    });
  }, [formState, toggles, imageState.dealImage, imageState.imageFile]);

  useEffect(() => {
    if (initialFormSnapshotRef.current === null) {
      initialFormSnapshotRef.current = getFormSnapshot();
    }
  }, [getFormSnapshot]);

  const isDirty =
    initialFormSnapshotRef.current !== null &&
    getFormSnapshot() !== initialFormSnapshotRef.current;

  useEffect(() => {
    if (profileData) {
      setCompCurrency(profileData?.compCurrency || "GBP");
      setCreatedBy(profileData?.userName || "");
    }
  }, [profileData]);

  const handleInputChange = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handlePricingTypeChange = (newPricingType) => {
    setFormState((prev) => ({
      ...prev,
      dealPricingType: newPricingType,
      dealPrice: newPricingType === "CustomPrice" ? prev.dealPrice : "",
      dealDiscountPercentage:
        newPricingType === "Discount" ? prev.dealDiscountPercentage : "",
    }));
    if (errors.dealPrice) {
      setErrors((prev) => ({ ...prev, dealPrice: "" }));
    }
  };

  const handleToggleChange = (field, value) => {
    setToggles((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formState.dealName.trim()) {
      newErrors.dealName = "Deal name is required";
    }

    if (formState.dealPricingType === "CustomPrice") {
      if (!formState.dealPrice || formState.dealPrice.toString().trim() === "") {
        newErrors.dealPrice = "Deal price is required for Custom Price";
      } else if (parseFloat(formState.dealPrice) < 0) {
        newErrors.dealPrice = "Deal price cannot be negative";
      }
    } else if (formState.dealPrice && parseFloat(formState.dealPrice) < 0) {
      newErrors.dealPrice = "Deal price cannot be negative";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (options = {}) => {
    const { exitAfterSave = false } = options;

    if (!validateForm()) {
      showErrorToast("Please fix the errors in the form");
      return false;
    }

    try {
      setLoading(true);

      let imageUrl = imageState.dealImage;
      const baseURL = axiosInstance.defaults.baseURL || "";
      try {
        if (imageState.imageFile) {
          const imageFormData = new FormData();
          imageFormData.append("ImageFile", imageState.imageFile);
          const imageResponse = await axiosInstance.post(
            APIs.POST_UPLOAD_PRODUCT_IMAGE,
            imageFormData,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
          const relativePath = imageResponse.data?.data?.filePath;
          if (relativePath) {
            imageUrl = relativePath;
          }
        } else if (imageState.dealImage && !imageState.dealImage.startsWith("http")) {
          const baseUrlClean = baseURL.endsWith("/") ? baseURL.slice(0, -1) : baseURL;
          imageUrl = imageState.dealImage.replace(baseUrlClean, "").replace(/^\/+/, "");
        } else if (imageState.dealImage && imageState.dealImage.startsWith("http")) {
          const baseUrlClean = baseURL.endsWith("/") ? baseURL.slice(0, -1) : baseURL;
          imageUrl = imageState.dealImage.replace(baseUrlClean, "").replace(/^\/+/, "");
        }
      } catch (err) {
        showErrorToast("Failed to upload image");
      }

      let formattedStartDate = null;
      let formattedEndDate = null;

      if (formState.startDate) {
        const startDate =
          formState.startDate instanceof Date
            ? formState.startDate
            : formState.startDate instanceof moment
              ? formState.startDate.toDate()
              : new Date(formState.startDate);
        const startMoment = moment(startDate);
        if (startMoment.isValid()) {
          formattedStartDate = startMoment.format("YYYY-MM-DDTHH:mm:ss");
        }
      }

      if (formState.endDate) {
        const endDate =
          formState.endDate instanceof Date
            ? formState.endDate
            : formState.endDate instanceof moment
              ? formState.endDate.toDate()
              : new Date(formState.endDate);
        const endMoment = moment(endDate);
        if (endMoment.isValid()) {
          formattedEndDate = endMoment.format("YYYY-MM-DDTHH:mm:ss");
        }
      }

      const pricingType = (formState.dealPricingType || "").trim();
      const dealData = {
        dealID: 0,
        compID: CompID,
        dealName: formState.dealName.trim(),
        dealDescription: formState.dealDescription.trim() || null,
        dealImage: imageUrl || null,
        dealPrice:
          pricingType === "CustomPrice" && formState.dealPrice
            ? parseFloat(formState.dealPrice)
            : null,
        isActive: toggles.isActive,
        showInKiosk: toggles.showInKiosk,
        showInPOS: toggles.showInPOS,
        showInOnline: toggles.showInOnline,
        dealOrder: parseInt(formState.dealOrder) || 0,
        pricingType: formState.dealPricingType || null,
        discountPercentage:
          pricingType === "Discount" && formState.dealDiscountPercentage
            ? parseFloat(formState.dealDiscountPercentage)
            : null,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        createdBy: createdBy,
        modifiedBy: null,
      };

      const response = await axiosInstance.post(APIs.POST_ADD_OR_UPDATE_DEAL, dealData);

      if (response.data.status) {
        showSuccessToast("Deal created successfully! Next: add schedule and slots.");
        initialFormSnapshotRef.current = getFormSnapshot();
        const newDealId = response.data.data?.dealID;
        if (exitAfterSave) {
          navigate("/deal/manageDeals");
        } else if (newDealId) {
          navigate(`/deal/updateDeal/${newDealId}`, {
            state: { activeTab: "slots" },
          });
        } else {
          navigate("/deal/manageDeals");
        }
        return true;
      } else {
        showErrorToast(response.data.message || "Failed to create deal");
        return false;
      }
    } catch (error) {
      console.error("Error creating deal:", error);
      showErrorToast("Something went wrong. Please try again.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  useUnsavedChangesGuard({
    isDirty,
    onSave: () => handleSubmit({ exitAfterSave: false }),
    onDiscard: () => {
      initialFormSnapshotRef.current = getFormSnapshot();
    },
  });

  return (
    <div className="deal-form-page" style={{ "--df-accent": themeColor }}>
      <div className="deal-form-page__head">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/deal/manageDeals">Manage Deals</Link>
            </li>
            <li className="breadcrumb-item active">Create Deal</li>
          </ol>
          <h1 className="deal-form-page__title">
            <i className="fa fa-tags deal-form-page__title-icon" aria-hidden="true" />
            Create Deal
          </h1>
          <p className="deal-form-page__sub">
            Set the basics now. You can add weekly schedule and slots right after creating.
          </p>
        </div>
        <div className="deal-form-page__actions">
          <button
            type="button"
            className="deal-form-page__cta"
            style={{
              background: "#dc3545",
              border: "none",
              color: "#fff",
            }}
            onClick={() => navigate("/deal/manageDeals")}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="deal-form-page__cta deal-form-page__cta--secondary"
            onClick={() => handleSubmit({ exitAfterSave: false })}
            disabled={loading}
          >
            <i className="fa fa-save" aria-hidden="true" />
            {loading ? "Saving..." : "Save"}
          </button>
          <button
            type="button"
            className="deal-form-page__cta"
            onClick={() => handleSubmit({ exitAfterSave: true })}
            disabled={loading}
          >
            <i className="fa fa-save" aria-hidden="true" />
            {loading ? "Saving..." : "Save and Exit"}
          </button>
        </div>
      </div>

      <GridSectionLoader loading={loading} minHeight="400px">
        <form
          id="create-deal-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit({ exitAfterSave: false });
          }}
        >
          <DealFormBasics
            themeColor={themeColor}
            formState={formState}
            toggles={toggles}
            imageState={imageState}
            setImageState={setImageState}
            errors={errors}
            handleInputChange={handleInputChange}
            handlePricingTypeChange={handlePricingTypeChange}
            handleToggleChange={handleToggleChange}
            dropdownStyles={dropdownStyles}
            getCurrencySymbol={getCurrencySymbol}
            showMinimumOrder={false}
          />
        </form>
      </GridSectionLoader>
    </div>
  );
};

export default CreateDeal;
